<!--
 * @Description: SEO 内容模块（仅未登录用户展示）
-->
<template>
    <div class="seo-content">
        <!-- 展开按钮（仅未展开时显示）—— SEO 用，折叠态保持极简 -->
        <div v-show="!isExpanded" class="flex justify-center">
            <button class="seo-expand-btn seo-expand-btn--mini" :aria-label="$t('seoContent.expandMore')" @click="isExpanded = true">
                {{ $t('seoContent.expandMore') }}
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
        </div>

        <!-- 展开内容 -->
        <div v-show="isExpanded" class="expanded-content">
            <!-- 第一部分：标题介绍 -->
            <div class="intro-section text-center seo-section-spacing fade-in-section">
                <h2 class="seo-main-title">
                    {{ $t('seoContent.mainTitle') }}
                </h2>
                <p class="seo-description mx-auto">
                    {{ $t('seoContent.mainDesc1') }}
                </p>
            </div>

            <!-- 第二部分：Why Choose 微鲤AI -->
            <div class="features-section seo-section-spacing fade-in-section">
                <div class="text-center mb-10">
                    <h2 class="seo-h2-title">
                        {{ $t('seoContent.whyChooseTitle') }}
                    </h2>
                    <p class="seo-description mx-auto seo-desc-pre-line">
                        {{ $t('seoContent.whyChooseDesc') }}
                    </p>
                </div>

                <!-- 特性卡片网格 -->
                <div class="features-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div v-for="(feature, index) in features" :key="index" class="feature-card">
                        <div class="icon-wrapper w-10 h-10 mb-4">
                            <img :src="feature.icon" :alt="feature.title" class="w-full h-full object-contain"
                                width="60" height="60" loading="lazy" />
                        </div>
                        <h3 class="text-white font-semibold text-base mb-2">
                            {{ feature.title }}
                        </h3>
                        <p class="seo-description leading-relaxed whitespace-pre-line">
                            {{ feature.description }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- How to Use 微鲤AI -->
            <div class="seo-how-to-use seo-section-spacing fade-in-section">
                <div class="seo-how-container">
                    <div class="seo-how-header">
                        <h2 class="seo-h2-title">
                            {{ $t('seoContent.howToUseTitle') }}
                        </h2>
                    </div>

                    <div class="seo-steps-container">
                        <div v-for="(step, index) in howToUseSteps" :key="index" class="seo-step-item">
                            <div class="seo-step-number">{{ index + 1 }}</div>
                            <div class="seo-step-content">
                                <h3 class="seo-step-title">{{ step.title }}</h3>
                                <p class="seo-description">
                                    {{ step.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 第三部分：People Also Ask -->
            <div class="seo-section-spacing fade-in-section">
                <div class="seo-faq-header">
                    <h2 class="seo-h2-title">
                        {{ $t('seoContent.faqTitle') }}
                    </h2>
                    <p class="seo-description seo-desc-pre-line">
                        {{ $t('seoContent.faqDesc') }}
                    </p>
                </div>

                <div class="seo-faq-grid">
                    <!-- 左列 -->
                    <div class="seo-faq-column">
                        <div v-for="(faq, index) in leftFaqs" :key="'left-' + index" class="seo-faq-item" :class="{
                            'is-expanded': isFaqExpanded('left', index),
                        }" @click="toggleFaq('left', index)">
                            <div class="seo-faq-question">
                                <span class="seo-faq-question-text">{{
                                    faq.question
                                    }}</span>
                                <span class="seo-faq-toggle">
                                    {{
                                        isFaqExpanded('left', index) ? '×' : '+'
                                    }}
                                </span>
                            </div>
                            <div v-if="isFaqExpanded('left', index)" class="seo-faq-answer">
                                <p>{{ faq.answer }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- 右列 -->
                    <div class="seo-faq-column">
                        <div v-for="(faq, index) in rightFaqs" :key="'right-' + index" class="seo-faq-item" :class="{
                            'is-expanded': isFaqExpanded('right', index),
                        }" @click="toggleFaq('right', index)">
                            <div class="seo-faq-question">
                                <span class="seo-faq-question-text">{{
                                    faq.question
                                    }}</span>
                                <span class="seo-faq-toggle">
                                    {{
                                        isFaqExpanded('right', index)
                                            ? '×'
                                            : '+'
                                    }}
                                </span>
                            </div>
                            <div v-if="isFaqExpanded('right', index)" class="seo-faq-answer">
                                <p>{{ faq.answer }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 收起按钮 -->
            <div class="flex justify-center mt-10">
                <button class="seo-expand-btn" :aria-label="$t('seoContent.collapse')" @click="isExpanded = false">
                    {{ $t('seoContent.collapse') }}
                    <svg class="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from '#imports'

const { t } = useI18n()

const isExpanded = ref(false)
const expandedFaq = ref<string | null>(null)

// 滚动淡入动画 - Intersection Observer
let observer: IntersectionObserver | null = null

const initObserver = () => {
    observer = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    observer?.unobserve(entry.target) // 只触发一次
                }
            })
        },
        {
            threshold: 0.1, // 10% 可见时触发
            rootMargin: '0px 0px -50px 0px', // 提前 50px 触发
        }
    )
}

const observeElements = () => {
    document.querySelectorAll('.fade-in-section').forEach(el => {
        el.classList.remove('is-visible') // 重置状态
        observer?.observe(el)
    })
}

// 监听展开状态，展开时初始化观察
watch(isExpanded, newVal => {
    if (newVal) {
        nextTick(() => {
            observeElements()
        })
    }
})

onMounted(() => {
    initObserver()
})

onUnmounted(() => {
    observer?.disconnect()
})

// 统一的排它逻辑（六个问题全部互斥）
const toggleFaq = (column: 'left' | 'right', index: number) => {
    const key = `${column}-${index}`
    expandedFaq.value = expandedFaq.value === key ? null : key
}

// 检查是否展开
const isFaqExpanded = (column: 'left' | 'right', index: number) => {
    return expandedFaq.value === `${column}-${index}`
}

// 8个特性卡片（使用 i18n）
const features = computed(() => [
    {
        icon: 'https://static.weryai.com/cms/Icon1.webp',
        title: t('seoContent.feature1Title'),
        description: t('seoContent.feature1Desc'),
    },
    {
        icon: 'https://static.weryai.com/cms/Icon2.webp',
        title: t('seoContent.feature2Title'),
        description: t('seoContent.feature2Desc'),
    },
    {
        icon: 'https://static.weryai.com/cms/Icon3.webp',
        title: t('seoContent.feature3Title'),
        description: t('seoContent.feature3Desc'),
    },
    {
        icon: 'https://static.weryai.com/cms/Icon4.webp',
        title: t('seoContent.feature4Title'),
        description: t('seoContent.feature4Desc'),
    },
    {
        icon: 'https://static.weryai.com/cms/Icon5.webp',
        title: t('seoContent.feature5Title'),
        description: t('seoContent.feature5Desc'),
    },
    {
        icon: 'https://static.weryai.com/cms/Icon6.webp',
        title: t('seoContent.feature6Title'),
        description: t('seoContent.feature6Desc'),
    },
    // {
    //     icon: 'https://static.weryai.com/cms/Icon7.webp',
    //     title: t('seoContent.feature7Title'),
    //     description: t('seoContent.feature7Desc'),
    // },
    // {
    //     icon: 'https://static.weryai.com/cms/Icon8.webp',
    //     title: t('seoContent.feature8Title'),
    //     description: t('seoContent.feature8Desc'),
    // },
])

// How to Use 步骤数据（使用 i18n）
const howToUseSteps = computed(() => [
    {
        title: t('seoContent.step1Title'),
        description: t('seoContent.step1Desc'),
    },
    {
        title: t('seoContent.step2Title'),
        description: t('seoContent.step2Desc'),
    },
    {
        title: t('seoContent.step3Title'),
        description: t('seoContent.step3Desc'),
    },
    {
        title: t('seoContent.step4Title'),
        description: t('seoContent.step4Desc'),
    },
])

// FAQ 数据（使用 i18n）
const faqs = computed(() => [
    {
        question: t('seoContent.faq1Question'),
        answer: t('seoContent.faq1Answer'),
    },
    {
        question: t('seoContent.faq2Question'),
        answer: t('seoContent.faq2Answer'),
    },
    {
        question: t('seoContent.faq3Question'),
        answer: t('seoContent.faq3Answer'),
    },
    {
        question: t('seoContent.faq4Question'),
        answer: t('seoContent.faq4Answer'),
    },
    {
        question: t('seoContent.faq5Question'),
        answer: t('seoContent.faq5Answer'),
    },
])

// 左列 FAQ（索引 0, 2, 4）
const leftFaqs = computed(() =>
    faqs.value.filter((_, index) => index % 2 === 0)
)

// 右列 FAQ（索引 1, 3, 5）
const rightFaqs = computed(() =>
    faqs.value.filter((_, index) => index % 2 === 1)
)
</script>

<style lang="less" scoped>
.seo-content {
    width: 1400px;
    max-width: 100%;
    padding: 8px 20px 0;
    margin: 0 auto;

    @media (max-width: 1440px) {
        width: 100%;
        padding: 8px 24px 0;
    }

    @media (max-width: 768px) {
        padding: 6px 16px 0;
    }

    @media (max-width: 480px) {
        padding: 4px 12px 0;
    }
}

// 展开内容区域
.expanded-content {
    padding-top: 300px;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 100vw;
        height: 1px;
        background: rgba(229, 231, 235, 0.1);
    }

    @media (max-width: 768px) {
        padding-top: 60px;
    }

    @media (max-width: 480px) {
        padding-top: 40px;
    }
}

// 各部分间距
.seo-section-spacing {
    margin-bottom: 300px;

    @media (max-width: 768px) {
        margin-bottom: 80px;
    }

    @media (max-width: 480px) {
        margin-bottom: 60px;
    }
}

// 主标题渐变文字样式
.seo-main-title {
    font-family: Roboto, sans-serif;
    font-size: 60px;
    font-style: normal;
    font-weight: 700;
    background: #ffffff;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        font-size: 36px;
    }

    @media (max-width: 480px) {
        font-size: 28px;
    }
}

// 描述文案样式
.seo-description {
    color: #adadad;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
}

.seo-desc-pre-line {
    white-space: pre-line;
}

// 统一 h2 标题样式
.seo-h2-title {
    color: #fff;
    font-family: Roboto, sans-serif;
    font-size: 48px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        font-size: 32px;
    }

    @media (max-width: 480px) {
        font-size: 24px;
    }
}

// 展开/收起按钮样式（渐变边框效果）
.seo-expand-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 30px;
    border-radius: 100px;
    background: #15171d;
    border: 1px solid transparent;
    color: #adadad;
    font-size: 16px;
    font-weight: 400;
    font-family: Roboto;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 100px;
        padding: 1px;
        background: linear-gradient(180deg,
                rgba(255, 255, 255, 0.1) 0%,
                rgba(255, 255, 255, 0) 100%);
        -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        mask-composite: exclude;
        z-index: 1;
        pointer-events: none;
    }

    &:hover {
        color: #ffffff;
    }

    svg {
        width: 16px;
        height: 16px;
    }
}

// 折叠态迷你按钮：仅作 SEO 入口，尽量不占视觉空间
.seo-expand-btn--mini {
    padding: 3px 12px;
    gap: 4px;
    font-size: 12px;
    line-height: 1.4;
    color: #6b6b6b;
    background: transparent;
    opacity: 0.6;
    transition: opacity 0.2s ease, color 0.2s ease;

    &::before {
        display: none;
    }

    &:hover {
        opacity: 1;
        color: #adadad;
    }

    svg {
        width: 12px;
        height: 12px;
    }
}

.feature-card {
    border-radius: 20px;
    background: #15171d;
    padding: 20px;
    transition: all 0.3s ease;
    position: relative;
    border: 1px solid transparent;

    &:hover {
        transform: translateY(-5px) scale(1.02);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    }

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 20px;
        padding: 1px;
        background: linear-gradient(180deg,
                rgba(255, 255, 255, 0.1) 0%,
                rgba(255, 255, 255, 0) 100%);
        -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        mask-composite: exclude;
        z-index: 1;
        pointer-events: none;
    }

    .icon-wrapper {
        width: 60px;
        height: 60px;
        margin-bottom: 16px;
    }
}

// How to Use 部分样式
.seo-how-to-use {
    background: #0d0e12;
}

.seo-how-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1rem;
}

.seo-how-header {
    text-align: center;
}

.seo-steps-container {
    display: flex;
    justify-content: space-between;
    position: relative;
    gap: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    padding: 5rem;
    border-radius: 8px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url('https://static.weryai.com/cms/Mask group.webp');
        background-size: cover;
        background-position: center;
        z-index: 1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to bottom,
                #0d0e12 0%,
                #0d0e12 10%,
                rgba(13, 14, 18, 0.7) 30%,
                rgba(13, 14, 18, 0.7) 50%,
                rgba(13, 14, 18, 0.3) 100%);
        z-index: 2;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1.5rem;
        padding: 3rem 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 2rem 1rem;
        gap: 1rem;
    }
}

.seo-step-item {
    width: 305px;
    flex: 1;
    text-align: left;
    backdrop-filter: blur(10px);
    background-color: rgba(20, 19, 19, 0.24);
    border-radius: 20px;
    padding: 20px 30px;
    position: relative;
    z-index: 4;

    @media (max-width: 768px) {
        padding: 1rem 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 0.875rem 1rem;
    }
}

.seo-step-number {
    width: 3rem;
    height: 3rem;
    position: relative;
    background-color: #252527;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    margin: 0 auto 0.75rem;
    margin-left: 5px;

    &::before {
        content: '';
        position: absolute;
        top: -5px;
        left: -5px;
        right: -5px;
        bottom: -5px;
        border-radius: 50%;
        box-shadow: 4px 4px 6px rgba(255, 255, 255, 0.4) inset;
        z-index: -1;
        border-top: 1px solid rgba(255, 255, 255, 0.8);
    }

    @media (max-width: 768px) {
        width: 2.5rem;
        height: 2.5rem;
        font-size: 1.25rem;
    }

    @media (max-width: 480px) {
        width: 2rem;
        height: 2rem;
        font-size: 1rem;

        &::before {
            top: -3px;
            left: -3px;
            right: -3px;
            bottom: -3px;
        }
    }
}

.seo-step-content {
    text-align: left;
}

.seo-step-title {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: white;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }

    @media (max-width: 480px) {
        font-size: 0.85rem;
    }
}

.seo-step-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }

    @media (max-width: 480px) {
        font-size: 0.75rem;
    }
}

// FAQ 部分样式（使用 seo-faq- 前缀避免全局样式冲突）
.seo-faq-section {
    margin-bottom: 150px;

    @media (max-width: 768px) {
        margin-bottom: 80px;
    }

    @media (max-width: 480px) {
        margin-bottom: 60px;
    }
}

.seo-faq-header {
    text-align: center;
    margin-bottom: 40px;
}

.seo-faq-subtitle {
    color: #a0a0a0;
    font-size: 14px;
    max-width: 768px;
    margin: 0 auto;
    line-height: 1.6;
}

.seo-faq-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.seo-faq-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.seo-faq-item {
    border-radius: 20px;
    background: #15171d;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    border: 1px solid transparent;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 20px;
        padding: 1px;
        background: linear-gradient(180deg,
                rgba(255, 255, 255, 0.1) 0%,
                rgba(255, 255, 255, 0) 100%);
        -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        mask-composite: exclude;
        z-index: 1;
        pointer-events: none;
    }
}

.seo-faq-question {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.seo-faq-question-text {
    color: #fff;
    font-family: Roboto, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    flex: 1;
    padding-right: 16px;
}

.seo-faq-toggle {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #99f768;
    font-size: 20px;
    font-weight: 600;
}

.seo-faq-answer {
    margin-top: 16px;

    p {
        color: #adadad;
        font-family: Roboto, sans-serif;
        font-weight: 400;
        line-height: 1.6;
    }
}

// 滚动淡入动画
.fade-in-section {
    opacity: 0;
    transform: translateY(30px);
    transition:
        opacity 0.6s ease-out,
        transform 0.6s ease-out;

    &.is-visible {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Screen reader only - 视觉隐藏但对 SEO 和屏幕阅读器可见 */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
